// Die Growbox (AC Infinity) aus dem Homelab-Protokoll (Topics `growbox.stand`,
// `growbox.lage`; docs/protokoll.md › „Growbox über das Protokoll“) in genau die
// Form, die die Seite aus der REST-Antwort GET /v2-api/acinfinity kennt
// (`AcInfinitySnapshot`, `AcInfinityDevice`, `AcInfinityPort`).
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/growbox-protokoll.test.mjs) sie laden kann. Die Stände kommen skaliert an
// (`skaliere("growbox.stand", …)`: °C, %, kPa); Gegenstück der Rechnung ist
// internal/protokoll/adapter/growbox.go.
//
// Regel, mit der die REST-Antwort exakt wiederkehrt (der Adapter sendet
// spiegelbildlich):
//   - Felder ohne `omitempty` (id, name, mac, online, ports, sensors, jedes
//     Feld eines Ports, configured, authenticated, phases, last_poll, email):
//     fehlt der Wert im Stand, wird er ""/0/false/[]. (Der Adapter lässt leere
//     Texte weg, Zahlen und Wahrheitswerte ohne omitempty sendet er immer.)
//   - `sensors` ist ein Objekt mit genau den Schlüsseln, die die Cloud lieferte:
//     was im Stand fehlt, fehlt auch hier; 0 bleibt ein Wert.
//   - `current_phase` (omitempty) steht nur, wenn der Stand eine Phase trägt.
//   - `last_poll` führt die REST-Antwort als time.Time: fehlt die Zeit im Stand
//     (noch nie abgerufen), setzt der Browser genau den Text wieder ein, den Go
//     für die Nullzeit schreibt ("0001-01-01T00:00:00Z").
//   - Die Id des Controllers steht nur im Instanznamen („growbox.1234.stand“);
//     die Reihenfolge der Cloud steht in `position`.

import type { AcInfinityDevice, AcInfinityPort, AcInfinitySnapshot } from "./acinfinity-summary.svelte";
import type { GrowboxLage, GrowboxPort, GrowboxStand } from "./protokoll/schema.gen";

/** So schreibt Go `time.Time{}` als JSON. */
export const NULLZEIT = "0001-01-01T00:00:00Z";

/** Die Lage des Zugangs, wie sie neben der Geräteliste in der REST-Antwort steht. */
export type Lage = Pick<AcInfinitySnapshot, "configured" | "authenticated" | "phases" | "last_poll" | "email">;

/** Instanzname → Id des Controllers: „growbox.1234.stand“ → „1234“. Die Lage
 *  (`growbox.lage`) und alles andere → undefined. */
export function growboxIdAusInstanz(instanz: string): string | undefined {
  const t = instanz.split(".");
  if (t.length !== 3 || t[0] !== "growbox" || t[2] !== "stand" || t[1] === "") return undefined;
  return t[1];
}

// Wert, wenn vorhanden (nicht null/undefined), sonst Rückfall.
const s = (v: string | null | undefined): string => v ?? "";
const n = (v: number | null | undefined): number => v ?? 0;
const b = (v: boolean | null | undefined): boolean => v ?? false;

function port(p: GrowboxPort): AcInfinityPort {
  return {
    port: n(p.port),
    name: s(p.name),
    type: s(p.art),
    level: n(p.stufe),
    on_level: n(p.stufe_an),
    off_level: n(p.stufe_aus),
    on: b(p.an),
    mode: n(p.modus),
    surplus: n(p.ueberschuss),
  };
}

/** Der Controller aus seinem Stand, wie `GET /v2-api/acinfinity` ihn in
 *  `devices` führt. `id` kommt aus dem Instanznamen; die Position (`position`)
 *  steht nicht im Ergebnis (siehe `positionAusStand`). */
export function geraetAusProtokoll(id: string, g: GrowboxStand): AcInfinityDevice {
  const sensors: AcInfinityDevice["sensors"] = {};
  if (g.temperatur != null) sensors.temperature = g.temperatur;
  if (g.feuchte != null) sensors.humidity = g.feuchte;
  if (g.vpd != null) sensors.vpd = g.vpd;
  const o: AcInfinityDevice = {
    id,
    name: s(g.name),
    mac: s(g.mac),
    online: b(g.online),
    sensors,
    ports: (g.ports ?? []).map(port),
  };
  if (g.phase) o.current_phase = g.phase;
  return o;
}

/** Die Stelle des Controllers in der Liste der Cloud; fehlt sie → undefined. */
export function positionAusStand(g: GrowboxStand): number | undefined {
  return typeof g.position === "number" && Number.isFinite(g.position) ? g.position : undefined;
}

/** Die Lage aus ihrem Stand, wie `GET /v2-api/acinfinity` sie neben `devices`
 *  führt. */
export function lageAusProtokoll(l: GrowboxLage): Lage {
  const zeit = typeof l.letzter_abruf === "number" && Number.isFinite(l.letzter_abruf) ? new Date(l.letzter_abruf) : undefined;
  return {
    configured: b(l.eingerichtet),
    authenticated: b(l.angemeldet),
    phases: [...(l.phasen ?? [])],
    last_poll: zeit && !Number.isNaN(zeit.getTime()) ? zeit.toISOString() : NULLZEIT,
    email: s(l.email),
  };
}

/** Der Snapshot, wie die REST-Antwort ihn führt, aus Lage und Controllern (in
 *  der Reihenfolge der Cloud). */
export function snapshotAusProtokoll(lage: Lage, devices: AcInfinityDevice[]): AcInfinitySnapshot {
  return { ...lage, devices };
}

/** Die Reihenfolge der Cloud: nach Position, bei Gleichstand (oder ohne
 *  Position) nach Id. `position` liefert die Stelle zu einer Id (oder
 *  undefined = unbekannt, kommt ans Ende). */
export function vergleicheGeraete(
  position: (id: string) => number | undefined,
): (a: Pick<AcInfinityDevice, "id">, b: Pick<AcInfinityDevice, "id">) => number {
  return (a, b2) => {
    const pa = position(a.id) ?? Number.POSITIVE_INFINITY;
    const pb = position(b2.id) ?? Number.POSITIVE_INFINITY;
    if (pa !== pb) return pa < pb ? -1 : 1;
    if (a.id !== b2.id) return a.id < b2.id ? -1 : 1;
    return 0;
  };
}
